/** Profile → Account: who you are here, and what that involves.
 *
 * The screen answers two different questions with two cards. On the left is
 * identity — the name other drivers see, and the providers that can open this
 * account — because both are things you change. On the right is consequence:
 * what the account is currently sharing, and what it has contributed to the
 * shared map. Nothing on the right is editable; each figure that can be acted
 * on links to the screen that acts on it.
 *
 * The account document is read here rather than taken from the session. The
 * shell holds `/api/me` for the header, but this screen needs the identities,
 * the counts and the statistics as the handler writes them, and a screen that
 * renders a shape it did not fetch is a screen that breaks quietly when the
 * shape moves. The uploads behind the contribution list are a second request
 * and a soft one: if that listing fails the rest of the page still stands,
 * with the failure stated where the list would have been.
 *
 * Saving a display name refreshes the session as well as the local copy,
 * because the name is drawn in the header by something this screen does not
 * own and a stale header beside a saved field reads as a failed save.
 *
 * One thing on the right is editable after all, and has to be: whether the
 * account's live stream is public. It is the only opt-in in the spec with no
 * screen of its own — one column, one `PATCH /api/me` — and the spectate page
 * tells a turned-away visitor to turn it on "from their profile". So the
 * control lives in the card that reports it and the request is made here,
 * beside the rename, because both are the same handler and the same reply.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  deleteAccount,
  getAccount,
  listMyUploads,
  listSignInProviders,
  updateAccount,
} from '@/lib/api';
import { useMe } from '@/lib/session';
import type { Account, SignInProvider } from '@/lib/types';
import { Card, ErrorNote, Kicker, LoadingPanel, Page } from '@/components/ui';
import {
  AccountShares,
  DeleteAccountRow,
  IdentityRows,
  ProfileHeader,
  contributionsFrom,
  withUser,
  type TrackContribution,
} from '@/components/profile';

/** `src/routes/me.ts` refuses anything longer. */
const MAX_DISPLAY_NAME = 40;

/** Enough uploads to summarise a contributor's circuits without paging: the
 *  list below is one row per circuit, and nobody has surveyed hundreds. */
const UPLOAD_PAGE = 200;

export default function ProfileAccount() {
  const { refresh } = useMe();

  const [account, setAccount] = useState<Account | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [providers, setProviders] = useState<SignInProvider[] | null>(null);
  const [contributions, setContributions] = useState<TrackContribution[] | null>(null);
  const [contributionsError, setContributionsError] = useState<unknown>(null);

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  const [livePublicSaving, setLivePublicSaving] = useState(false);
  const [livePublicError, setLivePublicError] = useState<unknown>(null);

  const [creditPublicSaving, setCreditPublicSaving] = useState(false);
  const [creditPublicError, setCreditPublicError] = useState<unknown>(null);

  const live = useRef(true);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const document = await getAccount();
      if (!live.current) return;
      setAccount(document);
      setName(document.user.display_name);
    } catch (thrown) {
      if (live.current) setError(thrown);
      return;
    }

    // The sign-in providers are chrome: a failure costs the Link buttons
    // their certainty, not the page its content.
    try {
      const offered = await listSignInProviders();
      if (live.current) setProviders(offered);
    } catch {
      if (live.current) setProviders(null);
    }
  }, []);

  const loadContributions = useCallback(async () => {
    setContributionsError(null);
    setContributions(null);
    try {
      const page = await listMyUploads({ limit: UPLOAD_PAGE });
      if (live.current) setContributions(contributionsFrom(page.uploads));
    } catch (thrown) {
      if (live.current) setContributionsError(thrown);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadContributions();
  }, [load, loadContributions]);

  const trimmed = name.trim();
  const dirty = account !== null && trimmed !== account.user.display_name;

  const save = useCallback(async () => {
    if (!account || !dirty || trimmed.length === 0) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const user = await updateAccount({ display_name: trimmed });
      if (!live.current) return;
      setAccount(withUser(account, user));
      setName(user.display_name);
      setSaved(true);
      // The header outside this screen draws the same name.
      await refresh();
    } catch (thrown) {
      if (live.current) setSaveError(thrown);
    } finally {
      if (live.current) setSaving(false);
    }
  }, [account, dirty, trimmed, refresh]);

  const setLivePublic = useCallback(
    async (next: boolean) => {
      if (!account) return;
      setLivePublicSaving(true);
      setLivePublicError(null);
      try {
        const user = await updateAccount({ live_public: next });
        if (!live.current) return;
        // The card draws the flag from `stats`, and the reply is the user row
        // alone — see `withUser` for why both have to move together.
        setAccount(withUser(account, user));
      } catch (thrown) {
        if (live.current) setLivePublicError(thrown);
      } finally {
        if (live.current) setLivePublicSaving(false);
      }
    },
    [account],
  );

  const setCreditPublic = useCallback(
    async (next: boolean) => {
      if (!account) return;
      setCreditPublicSaving(true);
      setCreditPublicError(null);
      try {
        const user = await updateAccount({ credit_public: next });
        if (live.current) setAccount(withUser(account, user));
      } catch (thrown) {
        if (live.current) setCreditPublicError(thrown);
      } finally {
        if (live.current) setCreditPublicSaving(false);
      }
    },
    [account],
  );

  const remove = useCallback(async () => {
    await deleteAccount();
    // The cookie is gone; a full navigation is the only honest way to drop
    // everything already drawn from the session.
    window.location.assign('/tracks');
  }, []);

  if (error) {
    return (
      <Page max={1100}>
        <ErrorNote error={error} title="could not load your account" onRetry={() => void load()} />
      </Page>
    );
  }

  if (!account) {
    return (
      <Page max={1100}>
        <LoadingPanel label="loading your account" />
      </Page>
    );
  }

  return (
    <Page max={1100} className="flex flex-col gap-5">
      <ProfileHeader user={account.user} stats={account.stats} />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: 16,
          alignItems: 'start',
        }}
      >
        <Card as="section" label="Display name and sign-in identities" style={{ gap: 12 }}>
          <Kicker>display name</Kicker>

          <form
            style={{ display: 'flex', gap: 8 }}
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <label className="sr-only" htmlFor="display-name">
              Display name
            </label>
            <input
              id="display-name"
              className="input"
              value={name}
              maxLength={MAX_DISPLAY_NAME}
              disabled={saving}
              aria-invalid={saveError ? true : undefined}
              onChange={(event) => {
                setName(event.currentTarget.value);
                setSaved(false);
              }}
            />
            <button
              type="submit"
              className="btn btn-secondary"
              style={{ flex: 'none' }}
              disabled={saving || !dirty || trimmed.length === 0}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </form>

          {saveError ? <ErrorNote error={saveError} compact /> : null}

          <p style={{ margin: 0, fontSize: 11.5, color: 'var(--color-neutral-600)' }}>
            Shown on public sessions and the spectate page, and beside your surveys on a
            circuit’s page if you turn track credit on. Pull request bodies credit account counts
            only.
            <span role="status" aria-live="polite">
              {saved ? <span style={{ color: 'var(--color-ok)' }}> Saved.</span> : null}
            </span>
          </p>

          <Kicker className="mt-1.5">sign-in identities</Kicker>
          <IdentityRows identities={account.identities} providers={providers} />
        </Card>

        <AccountShares
          stats={account.stats}
          tokensLive={account.tokens.live}
          onSetLivePublic={(next) => void setLivePublic(next)}
          livePublicSaving={livePublicSaving}
          livePublicError={livePublicError}
          creditPublic={account.user.credit_public}
          onSetCreditPublic={(next) => void setCreditPublic(next)}
          creditPublicSaving={creditPublicSaving}
          creditPublicError={creditPublicError}
          contributions={contributions}
          contributionsError={contributionsError}
          onRetryContributions={() => void loadContributions()}
        />
      </div>

      <DeleteAccountRow onConfirm={remove} />
    </Page>
  );
}
